import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Copy, Eye, EyeOff, Check, Tag } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CodeRevealBox = ({ dealId, hasCode = false }) => {
  const { toast } = useToast();
  const [revealed, setRevealed] = useState(false);
  const [code, setCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleRevealCode = async () => {
    if (revealed) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/deals/${dealId}/reveal-code`);
      
      if (!response.data.has_code) {
        toast({
          title: "No Code Required",
          description: "This deal doesn't need a promo code!",
          variant: "default"
        });
        return;
      }

      if (response.data.is_expired) {
        toast({
          title: "Deal Expired",
          description: "This deal has expired and the code is no longer valid",
          variant: "destructive"
        });
        return;
      }

      setCode(response.data.promo_code);
      setRevealed(true);
      
      toast({
        title: "Code Revealed!",
        description: "Click to copy the code"
      });
    } catch (error) {
      console.error('Error revealing code:', error);
      toast({
        title: "Error",
        description: "Failed to reveal code",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      
      // Track code copy
      await axios.post(`${API}/deals/${dealId}/copy-code`);
      
      toast({
        title: "Code Copied!",
        description: `"${code}" copied to clipboard`,
      });

      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying code:', error);
      toast({
        title: "Copy Failed",
        description: "Please copy the code manually",
        variant: "destructive"
      });
    }
  };

  if (!hasCode) {
    return null;
  }

  return (
    <Card className="border-2 border-emerald-600 bg-emerald-50" data-testid="code-reveal-box">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 text-emerald-700">
          <Tag className="w-5 h-5" />
          <h3 className="font-semibold text-lg">Promo Code Available</h3>
        </div>

        {!revealed ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Click below to reveal your exclusive promo code
            </p>
            <Button
              onClick={handleRevealCode}
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              size="lg"
              data-testid="reveal-code-button"
            >
              <Eye className="mr-2" size={18} />
              {loading ? 'Revealing...' : 'Reveal Promo Code'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-white border-2 border-dashed border-emerald-600 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Your Promo Code:</p>
                  <p className="text-2xl font-bold text-emerald-700 font-mono tracking-wider" data-testid="revealed-code">
                    {code}
                  </p>
                </div>
                <Badge className="bg-emerald-600 text-white">
                  Active
                </Badge>
              </div>
            </div>

            <Button
              onClick={handleCopyCode}
              variant="outline"
              className="w-full border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              size="lg"
              data-testid="copy-code-button"
            >
              {copied ? (
                <>
                  <Check className="mr-2" size={18} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="mr-2" size={18} />
                  Copy Code
                </>
              )}
            </Button>

            <p className="text-xs text-center text-gray-500">
              Use this code at checkout to get your discount
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
